import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';

import { cn } from '@/utils/cn';

export type SimpleBarChartDatum = {
  label: string;
  value: number;
  color?: string;
  detail?: string;
  currency?: string;
};

export interface SimpleBarChartProps {
  data: SimpleBarChartDatum[];
  /** Format values for tooltip and accessibility. Defaults to String(value). */
  formatValue?: (value: number, item?: SimpleBarChartDatum) => string;
  /** Y-axis ticks. Defaults to `formatValue`. Use a compact money format on trends. */
  formatTick?: (value: number) => string;
  height?: number;
  className?: string;
  emptyLabel?: string;
  /** Vertical bars for trends (many points). Defaults to vertical. */
  orientation?: 'vertical' | 'horizontal';
}

const DEFAULT_BAR_COLOR = 'var(--brand-primary)';

const AXIS_TICK = {
  fill: 'var(--text-tertiary)',
  fontSize: '0.75rem',
} as const;

/**
 * Horizontal: ranked tracks we draw ourselves so long fund names use the card width.
 * Vertical: Recharts, wrapped and painted with Trinity tokens — collision-aware
 * ticks, a box that fills the card, amount on hover.
 */
export const SimpleBarChart = ({
  data,
  formatValue = String,
  formatTick,
  height = 200,
  className,
  emptyLabel,
  orientation = 'vertical',
}: SimpleBarChartProps) => {
  if (data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center text-body-sm text-fg-secondary',
          className,
        )}
        style={{ height }}
      >
        {emptyLabel ?? null}
      </div>
    );
  }

  const max = Math.max(...data.map((d) => d.value), 0);
  const scaleMax = max > 0 ? max : 1;

  if (orientation === 'horizontal') {
    return (
      <div
        className={cn('flex w-full min-w-0 flex-col justify-start gap-4', className)}
        style={{ minHeight: Math.min(height, 48 + data.length * 44) }}
      >
        {data.map((item, index) => {
          const pct = (item.value / scaleMax) * 100;
          const formatted = formatValue(item.value, item);
          const color = item.color ?? DEFAULT_BAR_COLOR;
          const aria = `${item.label}: ${formatted}`;

          return (
            <div key={`${item.label}-${index}`} className="min-w-0 space-y-1.5">
              <div className="flex min-w-0 items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-caption text-fg-secondary" title={item.label}>
                  {item.label}
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-caption tabular-nums text-fg" title={formatted}>
                    {formatted}
                  </span>
                  {item.detail ? (
                    <span className="block text-caption tabular-nums text-fg-secondary">
                      {item.detail}
                    </span>
                  ) : null}
                </span>
              </div>
              <div
                className="h-2.5 w-full overflow-hidden rounded-full bg-surface-sunken"
                role="img"
                aria-label={aria}
                title={aria}
              >
                <div
                  className="h-full rounded-full motion-safe:transition-[width]"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: color,
                    minWidth: item.value > 0 ? 4 : 0,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <VerticalBarChart
      data={data}
      formatValue={formatValue}
      formatTick={formatTick}
      height={height}
      className={className}
    />
  );
};

const TrendTooltip = ({
  active,
  payload,
  formatValue,
}: {
  active?: boolean;
  payload?: TooltipContentProps['payload'];
  formatValue: (value: number, item?: SimpleBarChartDatum) => string;
}) => {
  if (!active || !payload?.[0]) return null;
  const item = payload[0].payload as SimpleBarChartDatum;
  return (
    <div className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-caption shadow-e2">
      <p className="text-fg-secondary">{item.label}</p>
      <p className="tabular-nums text-fg">{formatValue(item.value, item)}</p>
    </div>
  );
};

const VerticalBarChart = ({
  data,
  formatValue,
  formatTick,
  height,
  className,
}: {
  data: SimpleBarChartDatum[];
  formatValue: (value: number, item?: SimpleBarChartDatum) => string;
  formatTick?: (value: number) => string;
  height: number;
  className?: string;
}) => {
  const tickFormat = formatTick ?? ((value: number) => formatValue(value));
  const summary = data
    .map((item) => `${item.label}: ${formatValue(item.value, item)}`)
    .join('. ');
  const rows = data;

  return (
    <div
      className={cn('w-full min-w-0', className)}
      style={{ height }}
      role="img"
      aria-label={summary}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 8, right: 8, left: 4, bottom: 0 }}
          barCategoryGap="18%"
        >
          <CartesianGrid
            vertical={false}
            stroke="var(--border-default)"
            strokeOpacity={0.45}
          />
          <XAxis
            dataKey="label"
            interval="equidistantPreserveStart"
            minTickGap={22}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: 'var(--border-default)' }}
            height={28}
          />
          <YAxis
            width={64}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickFormatter={tickFormat}
          />
          <Tooltip
            cursor={{ fill: 'var(--surface-sunken)', opacity: 0.7 }}
            content={({ active, payload }) => (
              <TrendTooltip active={active} payload={payload} formatValue={formatValue} />
            )}
            wrapperStyle={{ outline: 'none', zIndex: 20 }}
          />
          <Bar
            dataKey="value"
            fill={DEFAULT_BAR_COLOR}
            radius={[2, 2, 0, 0]}
            maxBarSize={36}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
